import React, { useEffect, useMemo } from "react";
import { BreadcrumbItem, Nav, NavItem, NavbarText } from "reactstrap";
import { useQuery } from "@apollo/client";
import _get from "lodash/get";
import { Component as Form } from "@repoflow/form-schema";
import * as Notify from "@repoflow/form-schema/notify";
import { Mutation, Button } from "@repoflow/components/section";
import * as Preview from "@repoflow/components/preview";
import { Provider as EditorProvider } from "@repoflow/components/editor";
import { Layout } from "@repoflow/components/layout-dual";
import { Content as ContentAvailable } from "@repoflow/components/content-available";
import { ConfirmMutationModal } from "@repoflow/components/modal-confirm";
import { useNavbarContext } from "@repoflow/components/navbar";
import { useSidebarContext } from "@repoflow/components/sidebar-menu";
import ContentTabs, { useContentState } from "@repoflow/components/content-tabs";
import * as Loading from "@repoflow/components/loading";
import * as widgets from "widgets/register";
import * as SiteQL from "queries/site";
import * as BlogQL from "queries/blog";
import { TEMPLATE_LABELS } from "../../kinds";

import { Frame } from "./frame";

export const Sidebar = () => null;

export const Indice = () => (
  <>
    <BreadcrumbItem>Edit</BreadcrumbItem>
  </>
);

const SITE = "viewer.account.sites.get";

// The field list, hoisted out of the component for the reason the section
// editor records: an inline config literal is a new object every render, and
// the form rebuilds its widget tree under it between keystrokes.
const CODE_FIELDS = {
  "/Site/Blog/Template": {
    fields: [
      { property: "html", layout: "wide" },
      { property: "style", layout: "wide" },
      { property: "script", layout: "wide" },
    ],
  },
};

const TARGET = { $ref: "/Site/Blog/Template" };

// What a template can read, in one line per kind, above the code.
const SEES = {
  post: "values.title, abstract, image, tags, date, dateText, content, author, url",
  list: "values.items[] (each a post), page, hasPrev, hasNext, prevUrl, nextUrl, tag, empty",
  collection: "values.title, description, image, items[] (each a post), empty",
};

// THE TEMPLATE EDITOR: the section editor's Code tab beside the section
// editor's preview pane, for one of the site's three blog templates.
//
// It is that screen with the parts a template does not have taken out. No Data
// tab, because what a template sees is fixed by its kind rather than declared
// on it; no Details tab, because a template has no tags to be found by and no
// frame of its own — the element carries one. What is left is the three code
// fields, a Save in the tab strip for the reason the section editor puts it
// there, and a way back to the built-in.
//
// The row it edits is the SITE's — a template is a field of `site.blog` — so
// the mutation is the site's update, sent with the title it requires and this
// one kind under `blog.templates`; the model merges the rest (see
// api/code/src/model/site). The Authors screen writes the same field the same
// way.
export const Content = ({
  match: {
    params: { siteId, kind },
  },
}) => {
  const templatesResult = useQuery(BlogQL.templates, {
    variables: { siteId },
    fetchPolicy: "cache-and-network",
  });
  // Cache-first: the site route above already read it. The title is here
  // because the update insists on one.
  const siteResult = useQuery(SiteQL.get, { variables: { siteId } });

  const rows = _get(templatesResult.data, `${SITE}.blog.templates`) || [];
  const row = rows.find((template) => template.kind === kind) || null;
  const title = _get(siteResult.data, `${SITE}.title`) || "";

  const initial = useMemo(
    () => (row ? { html: row.html, style: row.style, script: row.script } : null),
    [row]
  );

  if (!row) {
    return <Loading.Content />;
  }

  return (
    <EditorProvider>
      <Preview.Provider>
        <Mutation
          // Remounted when the saved template changes, so a save's refetch
          // resets the form's baseline and Save goes grey again — the Authors
          // screen keys on its rows for the same reason. Typing changes the
          // form, not the row, so this is stable while editing.
          key={`${row.id}:${row.builtin}:${row.html.length}:${row.style.length}:${row.script.length}`}
          initial={initial}
          target={TARGET}
          mutation={SiteQL.edit}
          update={BlogQL.update()}
          refetch={[{ query: BlogQL.templates, variables: { siteId } }]}
          onMutate={({ handler, state: { values } }) => {
            handler({
              variables: {
                siteId,
                input: {
                  title,
                  blog: {
                    templates: {
                      [kind]: { html: values.html, style: values.style, script: values.script },
                    },
                  },
                },
              },
            });
          }}
          onCompleted={() => {
            Notify.success("Template saved");
          }}
          onError={(error) => {
            Notify.error(error.toString());
          }}
        >
          {/* A stable reference, never an inline arrow — see the section
              editor for the remount it would cause. */}
          {MutationHandler}
        </Mutation>
      </Preview.Provider>
    </EditorProvider>
  );
};

const CodeTab = ({ state, schema, mutate, kind }) => (
  <>
    <p className="text-muted small mt-2">
      This template reads <code>{SEES[kind]}</code>. The site's own values are{" "}
      <code>cxt.site.values</code>.
    </p>
    <Form
      schema={schema}
      target={TARGET}
      widgets={widgets}
      values={state.values}
      onChange={state.setValues}
      onSubmit={mutate}
      config={CODE_FIELDS}
    />
  </>
);

const MutationHandler = (props) => {
  const {
    state,
    attributes: { siteId, kind },
  } = props;
  const navbar = useNavbarContext();
  const { setOpen } = useSidebarContext();
  const tabs = useContentState({ selected: 0 });

  // Which template it is, and whether it is still the built-in, for the
  // "back to the built-in" button below. Read from the cache the parent just
  // filled — no second round trip.
  const templatesQuery = useQuery(BlogQL.templates, { variables: { siteId } });
  const siteResult = useQuery(SiteQL.get, { variables: { siteId } });
  const rows = _get(templatesQuery.data, `${SITE}.blog.templates`) || [];
  const row = rows.find((template) => template.kind === kind) || null;
  const title = _get(siteResult.data, `${SITE}.title`) || "";
  const label = TEMPLATE_LABELS[kind] || kind;

  // The editor wants the width. Collapsed on entry, restored on the way out,
  // as the section editor does.
  useEffect(() => {
    setOpen(false);
    return () => setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const Item = () => (
      <NavbarText className="ml-2">
        Blog template/<b>{label}</b>
      </NavbarText>
    );

    navbar.push(Item);
    return () => navbar.pop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label]);

  const items = [{ label: "Code", content: <CodeTab {...props} kind={kind} /> }];

  return (
    <Layout
      left={
        <>
          <div className="float-right">
            <Nav tabs>
              {row && !row.builtin ? (
                <NavItem>
                  {/* Back to the built-in. A confirmation, because it discards
                      markup somebody wrote and there is no undo: the row is
                      replaced, not versioned. `null` for the kind is how the
                      model is told to drop it — see mergeBlog. */}
                  <ConfirmMutationModal
                    className="nav-link text-danger"
                    label="Use the built-in"
                    mutation={SiteQL.edit}
                    variables={{ siteId, input: { title, blog: { templates: { [kind]: null } } } }}
                    update={BlogQL.update()}
                    // Re-read rather than trust the evictor alone: the row
                    // this screen is keyed on has to change for the editor to
                    // show the built-in it just went back to.
                    onCompleted={() => {
                      Notify.success("Built-in template restored");
                      templatesQuery.refetch();
                    }}
                    confirmation={
                      <p>
                        The {label.toLowerCase()} template goes back to the built-in one. What
                        is written here is discarded.
                      </p>
                    }
                  >
                    <p>
                      The {label.toLowerCase()} template goes back to the built-in one. What is
                      written here is discarded.
                    </p>
                  </ConfirmMutationModal>
                </NavItem>
              ) : null}
              <NavItem>
                <Button
                  enabledClass="nav-link text-success"
                  disabledClass="nav-link text-muted"
                >
                  Save
                </Button>
              </NavItem>
            </Nav>
          </div>

          <ContentTabs control={tabs} tabs={items} frame={TabFrame} />
        </>
      }
      right={<Frame siteId={siteId} kind={kind} state={state} />}
    />
  );
};

// Each tab scrolls inside the pane rather than growing it, as in the section
// editor.
const TabFrame = ({ children }) => (
  <ContentAvailable className="container-fluid px-2">{children}</ContentAvailable>
);
